import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import {
  PlotterBrand,
  PlotterBrandList,
  PlotterTarget,
  PrintingHistoryEntry,
  PrintingList,
  PrintingQuery,
  PrintingStatus,
  printPiecesQuery,
} from '../models/printing.model';
import { parseApiDecimal } from '../utils/api-decimal.utils';

type ApiPrintingHistoryEntry = Omit<PrintingHistoryEntry, 'printedArea'> & {
  printedArea: string;
};

type ApiPrintingList = {
  listing: ApiPrintingHistoryEntry[];
  total: number;
};

@Injectable({
  providedIn: 'root',
})
export class PrintingService {
  private readonly http = inject(HttpClient);

  private readonly route: Record<keyof PrintingQuery, string> = {
    ids: 'ids',
    modelIds: 'model-ids',
    userIds: 'user-ids',
    adminIds: 'admin-ids',
    startDate: 'start-date',
    endDate: 'end-date',
    offset: 'offset',
    limit: 'limit',
    exclude: 'exclude',
  };

  getPrintingList(query: PrintingQuery = {}): Observable<PrintingList> {
    let params = new HttpParams();

    for (const key of Object.keys(query) as (keyof PrintingQuery)[]) {
      const value = query[key];
      if (value == null) {
        continue;
      }

      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          params = params.append(`${this.route[key]}[${index}]`, String(item));
        });
      } else {
        params = params.set(this.route[key], String(value));
      }
    }

    return this.http.get<{ data: ApiPrintingList; statusCode: number }>('/api/printings/list', { params }).pipe(
      map(response => ({
        ...response.data,
        listing: response.data.listing.map(printing => ({
          ...printing,
          printedArea: parseApiDecimal(printing.printedArea),
        })),
      })),
    );
  }

  getAvailablePlotters(): Observable<PlotterBrand[]> {
    const url = '/api/plotter-brand/list';

    return this.http.get<{ data: PlotterBrandList; statusCode: number }>(url).pipe(
      map((response: { data: PlotterBrandList; statusCode: number }) => response.data.listing),
      catchError(() => of([])),
    );
  }

  print(query: printPiecesQuery, target: PlotterTarget): Observable<PrintingStatus> {
    return this.http
      .post<{ data: { status: PrintingStatus }; statusCode: number }>('/api/printings/print', { ...query, target })
      .pipe(map(response => response.data.status));
  }
}
